<template>
	<div id="socialStatsDisclaimers">
		<p>
			Any third party ratings reflect solely the opinions of the authors, do not reflect the views of Fundrise,
			and Fundrise does not independently certify the information contained therein. Any such content is meant for
			informational purposes only and is not intended to serve as a recommendation to buy or sell any security,
			and is not an offer or sale of any security, and is not intended to serve as the basis for any investment
			decision. Past performance is not indicative of future results, and all investments may result in total or
			partial loss.
		</p>
		<p>
			<strong>Apple App Store</strong> App Store rating is as of September 2025 and is based upon all ratings and
			reviews received by Apple App Store® since the Fundrise app was launched in April 2019. The current rating
			can be found on Fundrise’s App Store page and is subject to change. The App Store rating is calculated and
			generated by Apple based on the average of all total app reviews. No compensation was exchanged for reviews
			or ratings. The rating may not be representative of the Fundrise client experience, advisory services, or
			investment performance. For more information, see more reviews at the
			<BaseLink class="text-color-current-color text-decoration-underline" :link="appleAppStoreLink"
				>App Store</BaseLink
			>.
		</p>
		<p>
			<strong>Google Play Store</strong> Google Play Store rating is as of September 2025 and is based upon all
			ratings and reviews received by Google Play since the Fundrise app was launched in March 2020. The current
			rating can be found on Fundrise’s Google Play page and is subject to change. The Google Play Store rating is
			calculated and generated by Google based on the average of all total app reviews. No compensation was
			exchanged for reviews or ratings. The rating may not be representative of the Fundrise client experience,
			advisory services, or investment performance. For more information, see more reviews at the
			<BaseLink class="text-color-current-color text-decoration-underline" :link="googlePlayStoreLink"
				>Google Play Store</BaseLink
			>.</p
		>
		<p v-if="props.showBusinessInsider"
			><strong>Business Insider</strong> Fundrise received a 4-star rating from Business Insider in July 2024 for
			the duration of 2024. It was considered based on criteria including fees, investment options, customer
			service, and overall user experience. Information was gathered from Fundrise’s website. No compensation was
			exchanged for this rating. The author is not a client of Fundrise. Business Insider receives cash
			compensation for referring potential clients to Fundrise via advertisements placed on their respective
			websites. For more information, visit
			<BaseLink class="text-color-current-color text-decoration-underline" :link="businessInsiderLink">
				here</BaseLink
			>.
		</p>
		<p v-if="!appStore.isTablet && showInvestopedia"
			><strong>Investopedia</strong> Fundrise received a 4.7-star rating from Investopedia in December 2024. This
			recognition was based on an analysis of 19 platforms throughout 2024, evaluating criteria such as investment
			options, fees, transparency, and other features. No compensation was exchanged for this rating. The author
			is not a client of Fundrise. Investopedia receives cash compensation for referring potential clients to
			Fundrise via advertisements placed on their respective websites. For more information, visit
			<BaseLink class="text-color-current-color text-decoration-underline" :link="investopediaLink">here</BaseLink
			>.
		</p>
		<p v-if="showNerdwallet"
			><strong>Nerdwallet</strong> Fundrise received a 5-star rating from Nerdwallet in December 2024 for a review
			period of November - December 2024. The rating is based on over 15 factors, including account fees and
			minimums, investment choices, customer support, and mobile app capabilities. Fundrise replied to a
			Nerdwallet questionnaire that was used in the rating of this award. No compensation was exchanged for this
			rating. The author is not a client of Fundrise. Nerdwallet receives cash compensation for referring
			potential clients to Fundrise via advertisements placed on their respective websites. For more information,
			visit
			<BaseLink class="text-color-current-color text-decoration-underline" :link="nerdwalletLink">here</BaseLink>.
		</p>
		<br />
	</div>
</template>

<script setup lang="ts">
import { inject } from 'vue';
import { Link } from 'types/layout';
import { useAppStore } from '@stores/app';

interface Props {
	showBusinessInsider?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	showBusinessInsider: false
});

const appStore = useAppStore();

const showInvestopedia = inject('showInvestopedia', true);
const showNerdwallet = inject('showNerdwallet', true);

const appleAppStoreLink: Link = {
	href: 'https://apps.apple.com/us/app/fundrise-invest-in-alts/id1435387568',
	target: '_blank'
};

const googlePlayStoreLink: Link = {
	href: 'https://play.google.com/store/apps/details?id=com.fundrise.android&hl=en_US&pli=1',
	target: '_blank'
};

const businessInsiderLink: Link = {
	href: 'https://www.businessinsider.com/personal-finance/investing/fundrise-review#',
	target: '_blank'
};

const investopediaLink: Link = {
	href: 'https://www.investopedia.com/fundrise-review-8422703',
	target: '_blank'
};

const nerdwalletLink: Link = {
	href: 'https://www.nerdwallet.com/reviews/investing/brokers/fundrise',
	target: '_blank'
};
</script>
